/**
 * ----------------------------------------------------------------------------------------------------------------------
 * PRODUCT GALLERY COMPONENT - LOAD MORE FUNCTIONALITY
 * ----------------------------------------------------------------------------------------------------------------------
 * 
 * This script handles the "Load More" functionality for the product gallery component.
 * 
 * The product gallery component is used to show the product gallery in:
 * - Product page
 * - Featured product section
 * 
 * This specific script manages the behavior of the "Load More" button when:
 * - desktop_layout is set to "grid_2x"
 * - There are 7 or more visible images
 * 
 * It handles:
 * - Showing/hiding the "Load More" button based on visible image count
 * - Toggling visibility of images beyond the first 6
 * - Responding to variant changes that may affect image visibility
 * - Ensuring consistent behavior across page loads and cache states
 * - Excluding images with alt="card-only" from all calculations and display
 * - Hiding images based on market localization (-us-hide / -us-show in src)
 *
 * PRODUCT GALLERY COMPONENT - LOAD MORE FUNCTIONALITY
 * - Resets to collapsed state when variant changes
 * - No localStorage dependency
 * - Filters out card-only images
 * - Filters out market-specific images based on localization
 */

// Market handle is set by Liquid in the product-gallery.liquid template via window.currentMarketHandle.
// The US market UUID used in the Liquid file: '0a5ca24a-afcd-465e-b822-58498e2bbb6a'
const CURRENT_MARKET_HANDLE = window.currentMarketHandle || '';
const US_MARKET_HANDLE = '0a5ca24a-afcd-465e-b822-58498e2bbb6a';

/**
 * Returns true if a media wrapper div should be hidden based on market localization.
 * Mirrors the Liquid logic:
 *   - src contains '-us-hide' AND market is US  → hide
 *   - src contains '-us-show' AND market is NOT US → hide
 */
function isHiddenByMarket(div) {
  const src = div.dataset.mediaSrc || '';
  if (src.includes('-us-hide') && CURRENT_MARKET_HANDLE === US_MARKET_HANDLE) return true;
  if (src.includes('-us-show') && CURRENT_MARKET_HANDLE !== US_MARKET_HANDLE) return true;
  return false;
}

document.addEventListener("DOMContentLoaded", () => {

  // DOTS FIX: Re-hide market-specific dots after variant change
  function rehideMarketDots() {
    const isUS = window.currentMarketHandle === '0a5ca24a-afcd-465e-b822-58498e2bbb6a';

    document.querySelectorAll('.page-dots .tap-area').forEach(dot => {
      const position = dot.dataset.mediaPosition;
      const slide = document.querySelector(
        `.product-gallery__media[data-media-position="${position}"]`
      );
      if (!slide) return;

      const src = slide.dataset.mediaSrc || '';
      const shouldHide =
        (src.includes('-us-hide') && isUS) ||
        (src.includes('-us-show') && !isUS);

      if (shouldHide) {
        dot.setAttribute('hidden', '');
        dot.setAttribute('data-permanently-hidden', 'true');
      }
    });
  }

  rehideMarketDots();

  document.addEventListener('variant:change', () => {
    setTimeout(rehideMarketDots, 200);
  });
  // Function to handle the load more functionality
  function setupProductGallery() {
    const carousel = document.querySelector(".product .product-gallery__carousel");
    if (!carousel) return;
    
    const viewMoreBtn = document.querySelector(".view-more-btn");
    if (!viewMoreBtn) return;
    
    // Only run this code if we're in grid_2x layout
    const galleryElement = document.querySelector(".product .product-gallery");
    if (!galleryElement || !galleryElement.classList.contains("two-column-gallery")) {
      // Hide the button if we're not in grid_2x layout
      viewMoreBtn.style.display = "none";
      return;
    }

    const updateVisibleMediaItems = () => {
      // Get all media items that are not hidden AND don't have card-only images
      const allMediaItems = Array.from(carousel.querySelectorAll(".product-gallery__media:not([hidden])"));
      
      // Filter out items that contain card-only images OR should be hidden by market
      const visibleDivs = allMediaItems.filter(div => {
        // Hide card-only images
        const img = div.querySelector('img');
        if (img && (img.getAttribute('alt') || '').includes('card-only')) {
          div.style.display = 'none';
          return false;
        }

        // Hide market-specific images
        if (isHiddenByMarket(div)) {
          div.style.display = 'none';
          return false;
        }

        // Show the div
        div.style.display = '';
        return true;
      });

      const contents = document.querySelector('.product .product-gallery .product-gallery--carousel');
      if (!contents) return;

      // Only show the button if we have 7 or more visible items (excluding card-only)
      if (visibleDivs.length >= 7) {
        viewMoreBtn.style.display = "block";
        contents.classList.add("more-contents");
        
        // Always reset to collapsed state (remove "show" class)
        contents.classList.remove("show");
        
        // Add 'see-more-media' class to divs after the 6th index
        visibleDivs.forEach((div, index) => {
          if (index >= 6) {
            div.classList.add("see-more-media");
          } else {
            div.classList.remove("see-more-media");
          }
        });
      } else {
        // Hide the button if we have fewer than 7 visible items (excluding card-only)
        viewMoreBtn.style.display = "none";
        contents.classList.remove("more-contents");
        contents.classList.remove("show");
        
        // Remove 'see-more-media' class from all divs
        visibleDivs.forEach(div => {
          div.classList.remove("see-more-media");
        });
      }
    };

    // Function to hide card-only and market-hidden images immediately
    let isUpdating = false;

    const hideExcludedImages = () => {
      isUpdating = true;
      const allMediaItems = carousel.querySelectorAll(".product-gallery__media");
      allMediaItems.forEach(div => {
        const img = div.querySelector('img');
        if (img && (img.getAttribute('alt') || '').includes('card-only')) {
          div.style.display = 'none';
          return;
        }
        if (isHiddenByMarket(div)) {
          div.setAttribute('hidden', '');
          div.style.display = 'none';
          // Also hide the matching thumbnail
          const mediaId = div.dataset.mediaId;
          const thumb = document.querySelector(`.product-gallery__thumbnail[data-media-id="${mediaId}"]`);
          if (thumb) {
            thumb.setAttribute('hidden', '');
            thumb.style.display = 'none';
          }
        }
      });
      setTimeout(() => { isUpdating = false; }, 100);
    };
  

    // Hide excluded images immediately
    hideExcludedImages();
    
    // Initial update
    updateVisibleMediaItems();

    // Set up click handler for the view more button
    viewMoreBtn.addEventListener("click", () => {
      const contents = document.querySelector(".product .product-gallery .product-gallery--carousel");
      if (contents) {
        contents.classList.toggle("show");
      }
    });

    // Observe changes to the hidden attribute on gallery media items
    const observer = new MutationObserver((mutationsList) => {
      if (isUpdating) return;
      let shouldUpdate = false;
      
      for (const mutation of mutationsList) {
        if (mutation.type === "attributes" && 
            (mutation.attributeName === "hidden" || mutation.attributeName === "alt")) {
          shouldUpdate = true;
          break;
        }
        if (mutation.type === "childList") {
          shouldUpdate = true;
          break;
        }
      }
      
      if (shouldUpdate) {
        setTimeout(() => {
          hideExcludedImages();
          updateVisibleMediaItems();
        }, 50);
      }
    });

    // Observe all product gallery media items
    const mediaItems = carousel.querySelectorAll(".product-gallery__media");
    mediaItems.forEach(item => {
      observer.observe(item, { 
        attributes: true, 
        attributeFilter: ["hidden", "alt"],
        childList: true,
        subtree: true
      });
    });
    
    // Also observe the carousel for added/removed nodes (variant changes)
    observer.observe(carousel, { 
      childList: true,
      subtree: true
    });
    
    // Run when any variant selector changes
    const variantSelectors = document.querySelectorAll('.product-info .variant-picker__radio,.product-info select[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        // Reset gallery to collapsed state and update visibility
        const contents = document.querySelector(".product .product-gallery .product-gallery--carousel");
        if (contents) {
          contents.classList.remove("show");
        }
        
        setTimeout(() => {
          hideExcludedImages();
          updateVisibleMediaItems();
        }, 100);
      });
    });

    // Also listen for product form updates (alternative variant change detection)
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm) {
      productForm.addEventListener('change', () => {
        setTimeout(() => {
          hideExcludedImages();
          updateVisibleMediaItems();
        }, 100);
      });
    }
  }

  // Initial setup
  setupProductGallery();
  
  // Rerun setup after a slight delay to ensure everything is loaded
  setTimeout(setupProductGallery, 500);
});

// Add a single global click handler
document.addEventListener("click", (event) => {
  const viewMoreBtn = event.target.closest(".view-more-btn");
  if (viewMoreBtn) {
    const contents = document.querySelector(".product-gallery .product-gallery--carousel");
    if (contents) {
      contents.classList.toggle("show");
    }
  }
});